import { DEFAULT_CREDITS_ATTEMPTS, MINIMUM_POINTS_TO_PLAY } from '../prize-box-page.constants';
import {
  type HandType,
  type IPlayResponse,
  type IRaffleState,
  type WinStatus,
} from '../prize-box-page.types';
import { handToContractMove, statusFromWinner, synthesizeAiHand } from '../prize-box-page.utils';
import { MOCKED_PRIZE_CRATE_RAFFLE_ADAPTER_STATE } from 'e2e-tests/mocks/mock-rooms';
import { decodeEventLog, type PublicClient, type WalletClient } from 'viem';

import { MovesEnum } from 'shared/constants/shared-enums';
import { isMocked } from 'shared/utils/isMocked';

import {
  assertEnv,
  ERC20_ABI,
  QUEST_ADDRESS,
  QUESTS_ABI,
  RAFFLE_ABI,
  RAFFLE_ADDRESS,
} from './contracts';
import { parseUserData } from './parsers';
import { PrizeBoxError, PrizeBoxErrorType } from './prizebox-errors';
import { readMulticall } from './read-function';
import { toBigIntOr, tokenToCents, toNumberOr } from './units';

export async function ensurePreconditions(publicClient: PublicClient, account: `0x${string}`) {
  assertEnv();

  const requiredData = await readMulticall(publicClient, [
    { address: RAFFLE_ADDRESS, abi: RAFFLE_ABI, functionName: 'paused' },
    { address: RAFFLE_ADDRESS, abi: RAFFLE_ABI, functionName: 'shouldRestore', args: [account] },
    { address: RAFFLE_ADDRESS, abi: RAFFLE_ABI, functionName: 'getUser', args: [account] },
    {
      address: QUEST_ADDRESS,
      abi: QUESTS_ABI,
      functionName: 'getUserData',
      args: [account, MovesEnum.None],
    },
  ]);

  const [pausedRes, restoreRes, userRes, pointsRes] = requiredData;

  const paused = pausedRes.status === 'success' ? Boolean(pausedRes.result) : true;
  const shouldRestore = restoreRes.status === 'success' ? Boolean(restoreRes.result) : true;
  const parsedUser =
    userRes.status === 'success' ? parseUserData(userRes.result) : { attempts: 0, resetAt: 0n };
  const points = pointsRes.status === 'success' ? pointsRes.result : 0;

  if (paused) throw PrizeBoxError(PrizeBoxErrorType.Paused);

  // ─────────── points from Quests ───────────
  if (points < MINIMUM_POINTS_TO_PLAY) throw PrizeBoxError(PrizeBoxErrorType.InsufficientPoints);

  // ─────────── calculate attempts ───────────
  const latestBlock = await publicClient.getBlock();
  const nowMs = Number(latestBlock.timestamp ?? BigInt(Math.floor(Date.now() / 1000))) * 1000;
  const resetAtMs = Number(parsedUser.resetAt) * 1000;

  const usedAttempts =
    resetAtMs && nowMs >= resetAtMs
      ? 0
      : Math.min(Math.max(parsedUser.attempts, 0), DEFAULT_CREDITS_ATTEMPTS);

  const attemptsLeft = DEFAULT_CREDITS_ATTEMPTS - usedAttempts;

  if (!shouldRestore) return;

  if (attemptsLeft <= 0) {
    throw PrizeBoxError(PrizeBoxErrorType.CallCountExceeded);
  }
}

export async function getState(
  publicClient: PublicClient,
  account: `0x${string}`,
): Promise<IRaffleState & { attempts: number; needsRestore: boolean; contractReadable: boolean }> {
  assertEnv();

  let tokenDecimals = 6;
  let poolCents = 0;
  let durationSec = 0;
  let spendLimitCents = 0;
  let thresholdCents = 0;
  let currentSlotCents = 0;
  let lastSlotEndTimeMs = 0;
  let needsRestore = true;
  let contractReadable = true;

  let recoveryAmountRaw: bigint = 0n;
  let parsedUser: ReturnType<typeof parseUserData> = { attempts: 0, resetAt: 0n };

  try {
    const requiredCalls = [
      { address: RAFFLE_ADDRESS, abi: RAFFLE_ABI, functionName: 'balanceOf' },
      { address: RAFFLE_ADDRESS, abi: RAFFLE_ABI, functionName: 'slotConfig' },
      { address: RAFFLE_ADDRESS, abi: RAFFLE_ABI, functionName: 'currentSlot' },
      { address: RAFFLE_ADDRESS, abi: RAFFLE_ABI, functionName: 'recoveryAmount' },
      { address: RAFFLE_ADDRESS, abi: RAFFLE_ABI, functionName: 'getUser', args: [account] },
      { address: RAFFLE_ADDRESS, abi: RAFFLE_ABI, functionName: 'shouldRestore', args: [account] },
    ];

    const results = await readMulticall(publicClient, requiredCalls);

    const [poolRes, cfgRes, slotRes, recoveryRes, userRes, restoreRes] = results;

    // balanceOf
    if (poolRes.status === 'success' && Array.isArray(poolRes.result)) {
      const poolArray = poolRes.result as unknown[];
      const poolRaw = (poolArray[0] as bigint) ?? 0n;
      const decRaw = (poolArray[1] as bigint) ?? 6n;

      tokenDecimals = Number(decRaw);
      poolCents = tokenToCents(poolRaw, tokenDecimals);
    }

    // slotConfig & currentSlot
    const cfg = cfgRes.status === 'success' ? cfgRes.result : {};
    const data = slotRes.status === 'success' ? slotRes.result : {};

    const thresholdAmount = toBigIntOr(cfg?.thresholdAmount ?? cfg?.[0]);
    const maxSpendAmountPerOne = toBigIntOr(cfg?.maxSpendAmountPerOne ?? cfg?.[1]);
    const duration = toNumberOr(cfg?.duration ?? cfg?.[2]);

    const slotSpend = toBigIntOr(data?.spendAmount ?? data?.[2]);
    const slotStart = toNumberOr(data?.startTime ?? data?.[1]);

    durationSec = duration;
    spendLimitCents = tokenToCents(maxSpendAmountPerOne, tokenDecimals);
    thresholdCents = tokenToCents(thresholdAmount, tokenDecimals);
    currentSlotCents = tokenToCents(slotSpend, tokenDecimals);
    lastSlotEndTimeMs = (slotStart ? slotStart * 1000 : 0) + duration * 1000;

    // recoveryAmount
    recoveryAmountRaw = recoveryRes.status === 'success' ? BigInt(recoveryRes.result ?? 0n) : 0n;

    // getUser
    parsedUser =
      userRes.status === 'success' ? parseUserData(userRes.result) : { attempts: 0, resetAt: 0n };

    // shouldRestore
    needsRestore = restoreRes.status === 'success' ? Boolean(restoreRes.result) : true;

    const windowResetAtMs = Number(parsedUser.resetAt) * 1000;
    const nowMs = Date.now();

    const used =
      windowResetAtMs && nowMs >= windowResetAtMs
        ? 0
        : Math.min(Math.max(parsedUser.attempts, 0), DEFAULT_CREDITS_ATTEMPTS);
    let attempts = DEFAULT_CREDITS_ATTEMPTS - used;
    if (!needsRestore) attempts = Math.max(attempts, 1);

    if (isMocked) {
      return MOCKED_PRIZE_CRATE_RAFFLE_ADAPTER_STATE;
    }

    return {
      quotaResetAt: windowResetAtMs,
      extraCredits: 0,

      poolUsdt: poolCents,
      thresholdCents,
      currentSlotBalance: currentSlotCents,
      spendLimit: spendLimitCents,
      slotDurationSec: durationSec,
      lastSlotEndTime: lastSlotEndTimeMs,
      needsRestore,
      contractReadable,
      recoveryAmount: tokenToCents(recoveryAmountRaw, tokenDecimals),

      attempts,
    } as IRaffleState & { attempts: number; needsRestore: boolean; contractReadable: boolean };
  } catch {
    contractReadable = false;
    needsRestore = true;

    return {
      quotaResetAt: 0,
      extraCredits: 0,
      poolUsdt: 0,
      thresholdCents: 0,
      currentSlotBalance: 0,
      spendLimit: 0,
      slotDurationSec: 0,
      lastSlotEndTime: 0,
      needsRestore,
      contractReadable,
      recoveryAmount: 0,
      attempts: DEFAULT_CREDITS_ATTEMPTS,
    } as IRaffleState & { attempts: number; needsRestore: boolean; contractReadable: boolean };
  }
}

// ─────────── write: play (no simulate; parse logs) ─────────────────────────────
export async function play(
  hand: HandType,
  publicClient: PublicClient,
  walletClient: WalletClient | undefined,
  account: `0x${string}`,
): Promise<IPlayResponse & { potCents: number; txHash: `0x${string}` }> {
  assertEnv();
  if (!walletClient) throw PrizeBoxError(PrizeBoxErrorType.WALLET_UNAVAILABLE);

  await ensurePreconditions(publicClient, account);

  let decimals = 6;
  try {
    const [, decRaw] = (await publicClient.readContract({
      address: RAFFLE_ADDRESS,
      abi: RAFFLE_ABI,
      functionName: 'balanceOf',
      authorizationList: undefined,
    })) as readonly [bigint, bigint];
    decimals = Number(decRaw ?? 6);
  } catch {
    // fallback to 6 if it fails
  }

  const cmove = handToContractMove(hand);

  let gasWithBuffer: bigint | undefined;
  try {
    const gas = await publicClient.estimateContractGas({
      address: RAFFLE_ADDRESS,
      abi: RAFFLE_ABI,
      functionName: 'play',
      args: [cmove],
      account,
    });
    gasWithBuffer = ((gas > 300_000n ? gas : 300_000n) * 120n) / 100n;
  } catch {}

  // Send tx directly
  let txHash: `0x${string}`;
  try {
    txHash = (await walletClient.writeContract({
      address: RAFFLE_ADDRESS,
      abi: RAFFLE_ABI,
      functionName: 'play',
      args: [cmove],
      account,
      chain: undefined,
      ...(gasWithBuffer ? { gas: gasWithBuffer } : {}),
    })) as `0x${string}`;
  } catch (e: unknown) {
    const msg = String((e as Error)?.message || '').toLowerCase();
    if (msg.includes('notenoughpoints') || msg.includes('insufficientpoints'))
      throw PrizeBoxError(PrizeBoxErrorType.InsufficientPoints);
    if (msg.includes('requiredrestoreattempts') || msg.includes('callcountexceeded'))
      throw PrizeBoxError(PrizeBoxErrorType.CallCountExceeded);
    if (msg.includes('movenotallowed') || msg.includes('invalidmove'))
      throw PrizeBoxError(PrizeBoxErrorType.InvalidMove);
    if (msg.includes('enforcedpause') || msg.includes('paused'))
      throw PrizeBoxError(PrizeBoxErrorType.Paused);
    throw e instanceof Error ? e : PrizeBoxError(PrizeBoxErrorType.Unknown, 'Transaction failed');
  }

  const receipt = await publicClient.waitForTransactionReceipt({ hash: txHash });
  if (receipt.status !== 'success') throw PrizeBoxError(PrizeBoxErrorType.TransactionReverted);

  // Parse logs
  let winnerStatus = 0; // 0 draw, 1 win, 2 lose
  let winAmountRaw = 0n;
  let targetWinAmount = 0n;
  let addedPoints = 0;

  for (const log of receipt.logs) {
    try {
      const parsed = decodeEventLog({
        abi: RAFFLE_ABI,
        data: log.data,
        topics: (log as any).topics,
      });

      const { eventName, args } = parsed as { eventName: string; args: any };
      switch (eventName) {
        case 'Played': {
          const status = Number(args?.winStatus ?? args?.[2] ?? 0);
          if (!Number.isNaN(status)) winnerStatus = status;
          break;
        }
        case 'UserWinTokenAmount': {
          const rawUserWin = args?.userWinAmount ?? args?.[2] ?? 0n;
          const rawTargetWin = args?.targetAmount ?? args?.[1] ?? 0n;
          try {
            winAmountRaw = BigInt(rawUserWin);
            targetWinAmount = BigInt(rawTargetWin);
          } catch {
            winAmountRaw = 0n;
            targetWinAmount = 0n;
          }
          break;
        }
        case 'UserWinPoints': {
          const pts = Number(args?.points ?? args?.[1] ?? 0);
          addedPoints += pts | 0;
          break;
        }
        case 'UserLosePoints': {
          const pts = Number(args?.points ?? args?.[1] ?? 0);
          addedPoints -= Math.abs(pts | 0);
          break;
        }
      }
    } catch {
      // ignore non-matching logs
    }
  }

  // Re-sync state
  const state = await getState(publicClient, account);

  // Build response
  const status: WinStatus = statusFromWinner(winnerStatus);
  const aiHand = synthesizeAiHand(hand, status);
  const winAmount = tokenToCents(winAmountRaw, decimals);
  const totalWinAmount = tokenToCents(targetWinAmount, decimals);
  const potCents = state.currentSlotBalance;

  return {
    status,
    aiHand,
    pointsToWithdraw: 0,
    addedPoints,
    winAmount,
    totalAmount: totalWinAmount,
    state,
    potCents,
    txHash,
  };
}

// ────────────────────────────────────────────────────────────────────────────────
export async function ensureAllowanceForRestore(
  publicClient: PublicClient,
  walletClient: WalletClient,
  owner: `0x${string}`,
): Promise<`0x${string}` | undefined> {
  assertEnv();

  // ───────── multicall: token address + recoveryAmount ─────────
  const [tokenRes, recoveryRes] = await readMulticall(publicClient, [
    { address: RAFFLE_ADDRESS, abi: RAFFLE_ABI, functionName: 'token' },
    { address: RAFFLE_ADDRESS, abi: RAFFLE_ABI, functionName: 'recoveryAmount' },
  ]);

  if (tokenRes.status !== 'success' || recoveryRes.status !== 'success') {
    throw PrizeBoxError(PrizeBoxErrorType.ContractUnreadable);
  }

  const tokenAddr = tokenRes.result as `0x${string}`;
  const needed = BigInt(recoveryRes.result ?? 0n);

  // ───────── multicall: balanceOf, allowance, symbol ─────────
  const [balanceRes, allowanceRes, symbolRes] = await readMulticall(publicClient, [
    { address: tokenAddr, abi: ERC20_ABI, functionName: 'balanceOf', args: [owner] },
    {
      address: tokenAddr,
      abi: ERC20_ABI,
      functionName: 'allowance',
      args: [owner, RAFFLE_ADDRESS],
    },
    { address: tokenAddr, abi: ERC20_ABI, functionName: 'symbol' },
  ]);

  const tokenBalance = balanceRes.status === 'success' ? BigInt(balanceRes.result ?? 0n) : 0n;
  const allowance = allowanceRes.status === 'success' ? BigInt(allowanceRes.result ?? 0n) : 0n;
  const tokenSymbol = symbolRes.status === 'success' ? String(symbolRes.result) : 'TOKEN';

  if (tokenBalance < needed) {
    throw PrizeBoxError(PrizeBoxErrorType.NeedTopUp, `Need to top up your ${tokenSymbol} balance`);
  }

  if (allowance >= needed) return undefined;

  // ───────── prepare approve gas ─────────
  const [gasPrice, nativeBalance] = await Promise.all([
    publicClient.getGasPrice().catch(() => 0n),
    publicClient.getBalance({ address: owner }),
  ]);

  let approveGas = 65_000n;
  try {
    approveGas = await publicClient.estimateContractGas({
      address: tokenAddr,
      abi: ERC20_ABI,
      functionName: 'approve',
      args: [RAFFLE_ADDRESS, needed],
      account: owner,
    });
  } catch {
    // fallback
  }
  const approveGasWithBuffer = (approveGas * 120n) / 100n;
  const approveCost = approveGasWithBuffer * (gasPrice || 1n);
  if (nativeBalance < approveCost) throw PrizeBoxError(PrizeBoxErrorType.InsufficientGasForApprove);

  // ───────── send approve tx ─────────
  const hash = (await walletClient.writeContract({
    address: tokenAddr,
    abi: ERC20_ABI,
    functionName: 'approve',
    args: [RAFFLE_ADDRESS, needed],
    account: owner,
    chain: undefined,
    gas: approveGasWithBuffer,
  })) as `0x${string}`;

  await publicClient.waitForTransactionReceipt({ hash });
  return hash;
}

export async function restoreAttempts(
  publicClient: PublicClient,
  walletClient: WalletClient | undefined,
  account: `0x${string}`,
): Promise<
  IRaffleState & { approveTxHash?: `0x${string}`; restoreTxHash: `0x${string}`; attempts: number }
> {
  assertEnv();
  if (!walletClient) throw PrizeBoxError(PrizeBoxErrorType.WALLET_UNAVAILABLE);

  // ───────── multicall: shouldRestore, token, recoveryAmount ─────────
  const [restoreRes, tokenRes, neededRes] = await readMulticall(publicClient, [
    { address: RAFFLE_ADDRESS, abi: RAFFLE_ABI, functionName: 'shouldRestore', args: [account] },
    { address: RAFFLE_ADDRESS, abi: RAFFLE_ABI, functionName: 'token' },
    { address: RAFFLE_ADDRESS, abi: RAFFLE_ABI, functionName: 'recoveryAmount' },
  ]);

  const canRestore = restoreRes.status === 'success' ? Boolean(restoreRes.result) : true;
  if (!canRestore) throw PrizeBoxError(PrizeBoxErrorType.DoNotNeedToRestore);

  const tokenAddr = tokenRes.status === 'success' ? (tokenRes.result as `0x${string}`) : undefined;
  if (!tokenAddr) throw PrizeBoxError(PrizeBoxErrorType.TokenAddressUnavailable);

  const needed = neededRes.status === 'success' ? BigInt(neededRes.result ?? 0n) : 0n;

  // ───────── multicall: balanceOf, allowance, symbol ─────────
  const [balanceRes, allowanceRes] = await readMulticall(publicClient, [
    { address: tokenAddr, abi: ERC20_ABI, functionName: 'balanceOf', args: [account] },
    {
      address: tokenAddr,
      abi: ERC20_ABI,
      functionName: 'allowance',
      args: [account, RAFFLE_ADDRESS],
    },
    { address: tokenAddr, abi: ERC20_ABI, functionName: 'symbol' },
  ]);

  const tokenBalance = balanceRes.status === 'success' ? BigInt(balanceRes.result ?? 0n) : 0n;
  if (tokenBalance < needed) throw PrizeBoxError(PrizeBoxErrorType.InsufficientTokenBalance);

  const allowance = allowanceRes.status === 'success' ? BigInt(allowanceRes.result ?? 0n) : 0n;

  // ───────── ensure allowance ─────────
  let approveTxHash: `0x${string}` | undefined;
  if (allowance < needed) {
    approveTxHash = await ensureAllowanceForRestore(publicClient, walletClient, account);
  }

  // ───────── estimate restore gas ─────────
  let restoreGas = 150_000n;
  try {
    restoreGas = await publicClient.estimateContractGas({
      address: RAFFLE_ADDRESS,
      abi: RAFFLE_ABI,
      functionName: 'restore',
      args: [],
      account,
    });
  } catch {
    // fallback
  }

  const gasPrice = await publicClient.getGasPrice().catch(() => 0n);
  const nativeBalance = await publicClient.getBalance({ address: account });
  const restoreGasWithBuffer = (restoreGas * 120n) / 100n;
  const restoreCost = restoreGasWithBuffer * (gasPrice || 1n);
  if (nativeBalance < restoreCost) throw PrizeBoxError(PrizeBoxErrorType.InsufficientGasForRestore);

  // ───────── send restore tx ─────────
  let restoreTxHash: `0x${string}`;
  try {
    restoreTxHash = (await walletClient.writeContract({
      address: RAFFLE_ADDRESS,
      abi: RAFFLE_ABI,
      functionName: 'restore',
      args: [],
      account,
      chain: undefined,
      gas: restoreGasWithBuffer,
    })) as `0x${string}`;
  } catch (error: unknown) {
    const msg = String((error as Error)?.message || '').toLowerCase();
    if (msg.includes('notneedtorestoreattempts'))
      throw PrizeBoxError(PrizeBoxErrorType.DoNotNeedToRestore);
    if (msg.includes('notenoughpoints')) throw PrizeBoxError(PrizeBoxErrorType.InsufficientPoints);
    throw PrizeBoxError(PrizeBoxErrorType.FailedToRestore);
  }

  const receipt = await publicClient.waitForTransactionReceipt({ hash: restoreTxHash });
  if (receipt.status !== 'success') throw PrizeBoxError(PrizeBoxErrorType.TransactionReverted);

  const state = await getState(publicClient, account);
  return { ...state, approveTxHash, restoreTxHash, attempts: state.attempts } as any;
}

export async function getPoolBalance(
  publicClient: PublicClient,
): Promise<{ poolRaw: bigint; poolCents: number; decimals: number }> {
  if (!RAFFLE_ADDRESS) {
    return { poolRaw: 0n, poolCents: 0, decimals: 6 };
  }

  try {
    const [balance, decimals] = (await publicClient.readContract({
      address: RAFFLE_ADDRESS,
      abi: RAFFLE_ABI,
      functionName: 'balanceOf',
      authorizationList: undefined,
    })) as [bigint, bigint];

    const poolRaw = balance ?? 0n;
    const dec = Number(decimals ?? 6);
    const poolCents = tokenToCents(poolRaw, dec);

    return { poolRaw, poolCents, decimals: dec };
  } catch (err) {
    console.error('getPoolBalance error:', err);
    return { poolRaw: 0n, poolCents: 0, decimals: 6 };
  }
}
